import React, { useCallback, useContext, useEffect, useState } from 'react';
import classnames from 'classnames';
import { MediaEntry, MediaGalleryEntry } from 'src/types/global-types';
import { usePhotoswipeForGallery, useProductGallery } from 'src/hooks/useProductGallery';
import { useHistoryLocationObservable } from 'src/hooks/router-hooks';
import { distinctUntilKeyChanged } from 'rxjs';
import { StaticSpinner } from 'src/components/Layout/Spinner';
import { BundleOptionsContext } from 'src/components/BundleProduct/BundleOptionsProvider';
import { ImageSkeleton } from 'src/elements/Skeletons/Skeletons';
import { ITEMS_TO_SHOW_IN_CAROUSEL, CAROUSEL_OFFSET } from 'src/constants/productGallery';
import { EagerImg, LazyImg } from 'src/hooks/useLazyLoad';

import { Svg } from '../Svg/Svg';
import { Button } from '../Button/Button';

import classes from './ProductGallery.scss';
interface ProductGalleryProps {
    name: string;
    deliveryIcon: boolean;
    canCustomise: boolean;
    isBundle?: boolean;
    viewThumbnail?: boolean;
    modalGallery?: boolean;
    productVideo?: MediaGalleryEntry[];
}
enum mediaType {
    Video = 'video',
    Image = 'image',
}
export const ProductGalleryContext = React.createContext<{
    media_entries: MediaEntry[];
    setMediaEntries: (entries: MediaEntry[]) => void;
    setPending: () => void;
    pending: boolean;
}>({
    media_entries: [],
    setMediaEntries: (_entries: MediaEntry[]) => {
        console.warn('setMediaEntries not implemented');
    },
    setPending: () => {
        console.warn('setPending not implemented');
    },
    pending: false,
});
function sameMediaEntries(a: MediaEntry[], b: MediaEntry[]): boolean {
    return (
        a.length === b.length &&
        a.every(
            (entry, index) =>
                entry.medium_url === b[index].medium_url &&
                entry.small_url === b[index].small_url &&
                entry.large_url === b[index].large_url,
        )
    );
}

export const ProductGalleryProvider: React.FC<
    React.PropsWithChildren<{
        media_entries: MediaEntry[];
        bundleColor?: string[];
    }>
> = React.memo((props) => {
    const location$ = useHistoryLocationObservable();
    const [{ pending, media_entries }, setState] = useState({
        pending: false,
        media_entries: props.media_entries,
    });
    const { labelsWithOneBoundleImage } = useContext(BundleOptionsContext);
    const bundleImageBasedOnColor: string =
        props.bundleColor &&
        props.bundleColor.length > 0 &&
        labelsWithOneBoundleImage[props.bundleColor[props.bundleColor.length - 1]][0];
    const setMediaEntries = useCallback((entries: MediaEntry[]) => {
        setState((prev) => {
            if (sameMediaEntries(prev.media_entries, entries)) {
                return prev.pending ? { ...prev, pending: false } : prev;
            }
            return {
                pending: false,
                media_entries: entries,
            };
        });
    }, []);
    const setPending = useCallback(() => {
        setState((prev) => {
            return {
                ...prev,
                pending: true,
            };
        });
    }, []);
    useEffect(() => {
        let controlValue: string;
        const sub = location$
            .pipe(
                distinctUntilKeyChanged('search', (previous, next) => {
                    controlValue = previous;
                    return previous === next;
                }),
            )
            .subscribe(() => {
                if (controlValue) setPending();
            });
        return () => sub.unsubscribe();
    }, [location$, setPending]);
    // If we change boundle color, set image based on color
    useEffect(() => {
        if (props.bundleColor?.length === 0 || !bundleImageBasedOnColor) {
            setMediaEntries([...props.media_entries]);
            return;
        }
        const boundleMediaEntriesObj = {
            label: 'unknown',
            large_height: 720,
            large_url: bundleImageBasedOnColor,
            large_width: 720,
            media_type: mediaType.Image,
            medium_url: bundleImageBasedOnColor,
            position: 0,
            small_url: bundleImageBasedOnColor,
        };
        setMediaEntries([boundleMediaEntriesObj, ...props.media_entries]);
    }, [props.bundleColor, setMediaEntries, bundleImageBasedOnColor, JSON.stringify(props.media_entries)]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <ProductGalleryContext.Provider value={{ media_entries, pending, setMediaEntries, setPending }}>
            {props.children}
        </ProductGalleryContext.Provider>
    );
});
export const ProductGallery: React.FC<ProductGalleryProps> = ({
    name,
    deliveryIcon,
    canCustomise,
    isBundle,
    modalGallery,
    productVideo,
}) => {
    const { media_entries, pending } = useContext(ProductGalleryContext);

    /**
     * Product gallery state
     */
    const { sortedMediaEntries, selectedIndex, setSelected, mainRef, hasThumbs, mainImageLoading } =
        useProductGallery(media_entries);

    const getEmbedUrl = (url: string) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return `//www.youtube.com/embed/${match && match[2].length === 11 ? match[2] : null}`;
    };
    const computedSortedMediaEntries = [
        ...sortedMediaEntries,
        ...(productVideo?.map((video) => ({
            large_height: 85,
            large_width: 85,
            large_url: getEmbedUrl(video.video_content?.video_url || ''),
            medium_url: getEmbedUrl(video.video_content?.video_url || ''),
            small_url: `/media/catalog/product${video.file}`,
            media_type: mediaType.Video,
            label: 'unknown',
            position: 2,
        })) || []),
    ];

    const selected = computedSortedMediaEntries[selectedIndex];
    const totalLength = computedSortedMediaEntries.length;
    const videoIndex = computedSortedMediaEntries.findIndex((media) => media.media_type === mediaType.Video);
    /**
     * Photoswipe state
     */
    const { showPhotoswipe, photoswipeReady, photoSwipeRef, photoswipeHtml } = usePhotoswipeForGallery({
        selectedIndex,
        sortedMediaEntries: sortedMediaEntries,
    });

    const beyondSlides = totalLength - ITEMS_TO_SHOW_IN_CAROUSEL;
    const breakValueForCarousel = ITEMS_TO_SHOW_IN_CAROUSEL - 2;
    const carouselPrefix = selectedIndex + 1 - breakValueForCarousel + 1;

    const onChangeSlideView = (prefix: number) => {
        const galleryCarouselElement = document.getElementById('galleryCarousel');

        if (galleryCarouselElement) {
            if (prefix < 0) {
                galleryCarouselElement.scrollTop = -(prefix + 2) * CAROUSEL_OFFSET - CAROUSEL_OFFSET;
            } else {
                galleryCarouselElement.scrollTop = CAROUSEL_OFFSET * prefix;
            }
        }
    };

    return (
        <>
            {photoswipeReady && (
                <div
                    className={classes.modalGallery}
                    dangerouslySetInnerHTML={{ __html: photoswipeHtml }}
                    ref={photoSwipeRef}
                />
            )}
            <section className={classes.gallery}>
                {(hasThumbs || (productVideo && productVideo?.length > 0)) && (
                    <div className={classnames(classes.galleryThumbs, { [classes.isBundle]: isBundle })}>
                        <div className={classes.galleryShade} />

                        <div className={classes.galleryCarousel} id="galleryCarousel">
                            {computedSortedMediaEntries.map((item, index) => (
                                <div
                                    key={`${index}-${item.small_url}`}
                                    className={classnames(classes.thumb, {
                                        [classes.active]: index === selectedIndex,
                                    })}
                                    onClick={() => setSelected(index)}
                                >
                                    <LazyImg alt={item.label} src={item.small_url} />
                                </div>
                            ))}
                        </div>

                        <div className={classes.galleryShade} />
                    </div>
                )}
                <div className={classes.galleryViewPort}>
                    {media_entries.length === 0 ? (
                        <ImageSkeleton />
                    ) : (
                        <>
                            {deliveryIcon && <Svg className={classes.galleryDelivery} name="24hr" />}
                            {canCustomise && selected.media_type === mediaType.Image && (
                                <Svg className={classes.galleryAddIcon} name="addIcon" />
                            )}
                            {selected.media_type === mediaType.Image && videoIndex !== -1 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelected(videoIndex);
                                        onChangeSlideView(beyondSlides);
                                    }}
                                    className={classes.galleryVideoIcon}
                                >
                                    <img src={require('../../svg/video.svg')} alt="video svg" />
                                </button>
                            )}
                            {selected &&
                                (selected.media_type === mediaType.Image ? (
                                    <EagerImg
                                        className={classnames(classes.galleryImage, {
                                            [classes.galleryModal]: modalGallery,
                                        })}
                                        src={selected.medium_url}
                                        alt={name}
                                        data-loaded="true"
                                        ref={mainRef as any}
                                    />
                                ) : (
                                    <iframe
                                        className={classnames(classes.galleryImage, classes.video, {
                                            [classes.galleryModal]: modalGallery,
                                        })}
                                        width={`${selected.large_width}%`}
                                        height={`${selected.large_height}%`}
                                        src={selected.large_url}
                                        title="YouTube video player"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                    />
                                ))}
                            {totalLength > 0 && selected.media_type === mediaType.Image && (
                                <button
                                    type="button"
                                    aria-label="Zoom"
                                    className={classnames(classes.galleryZoom, { [classes.isBundle]: isBundle })}
                                    onClick={showPhotoswipe}
                                >
                                    <Svg name="zoom" />
                                </button>
                            )}
                            <span className={classes.gallerySpinner} data-active={String(pending || mainImageLoading)}>
                                <StaticSpinner />
                            </span>
                            <div className={classnames(classes.gallerySpacer, { [classes.isBundle]: isBundle })} />
                            {totalLength > 1 && (
                                <>
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            setSelected(selectedIndex === totalLength - 1 ? 0 : selectedIndex + 1);
                                            if (totalLength > ITEMS_TO_SHOW_IN_CAROUSEL) {
                                                if (
                                                    selectedIndex + 1 >= breakValueForCarousel &&
                                                    selectedIndex + 1 < totalLength - 2
                                                ) {
                                                    onChangeSlideView(carouselPrefix);
                                                }
                                                if (selectedIndex === totalLength - 1) {
                                                    onChangeSlideView(0);
                                                }
                                            }
                                        }}
                                        variant="reset"
                                        className={classes.galleryNext}
                                        aria-label="Next image"
                                    >
                                        <Svg name="arrow-right" />
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            setSelected(selectedIndex === 0 ? totalLength - 1 : selectedIndex - 1);
                                            if (totalLength > ITEMS_TO_SHOW_IN_CAROUSEL) {
                                                if (
                                                    totalLength - (selectedIndex + 1) >= 2 &&
                                                    selectedIndex + 1 > breakValueForCarousel
                                                ) {
                                                    onChangeSlideView(-carouselPrefix);
                                                }
                                                if (selectedIndex === 0) {
                                                    onChangeSlideView(beyondSlides);
                                                }
                                            }
                                        }}
                                        variant="reset"
                                        className={classes.galleryPrev}
                                        aria-label="Previous image"
                                    >
                                        <Svg name="arrow-left" />
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </section>
        </>
    );
};
